import { useState, useCallback } from 'react'
import {
  PaymentFilters,
  TerminatedFilter,
  TestUserFilter,
} from '../Utils/types'
import { PaymentAssignmentRole } from '../Utils/roles'
import { parsePaymentAssignmentFiltersFromUrl } from '../Utils/paymentAssignmentUrlState'

const SELECT_ALL_VALUE = '__SELECT_ALL__'

export function getDefaultPaymentPeriod(): string {
  const now = new Date()
  const date =
    now.getDate() >= 24
      ? new Date(now.getFullYear(), now.getMonth() + 1, 1)
      : now
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

const DEFAULT_TEST_USER_FILTER = TestUserFilter.NON_TEST_ONLY

export const usePaymentAssignmentFilters = (options?: {
  role?: PaymentAssignmentRole
  lockedAssigneeId?: number
  lockedEscalatorId?: number
}) => {
  const role = options?.role
  const lockedAssigneeId = options?.lockedAssigneeId
  const lockedEscalatorId = options?.lockedEscalatorId

  const [filters, setFilters] = useState<PaymentFilters>(() => ({
    paymentPeriod: getDefaultPaymentPeriod(),
    carrierNames: [],
    paymentTypes: [],
    priorityLevels: [],
    statuses: [],
    clientPublicKeys: [],
    assigneeIds: [], // Empty = all cases
    escalatorIds: [],
    paymentSubmittedByIds: [],
    primaryPaymentMethods: [],
    search: undefined,
    testUserFilter: DEFAULT_TEST_USER_FILTER,
    terminatedFilter: TerminatedFilter.ALL,
    relationships: [],
    // Restore any filters encoded in the URL so a shared link reproduces the
    // exact same filtered view.
    ...parsePaymentAssignmentFiltersFromUrl(),
  }))

  const updateFilter = useCallback(
    <K extends keyof PaymentFilters>(key: K, value: PaymentFilters[K]) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }))
    },
    []
  )

  const handleSelectAll = useCallback(
    (
      key: 'carrierNames' | 'paymentTypes' | 'statuses' | 'clientPublicKeys',
      allOptions: Array<string | number>,
      currentValue: Array<string | number>
    ) => {
      const cleanedCurrent = (currentValue ?? []).filter((v) =>
        typeof v === 'string' ? v !== SELECT_ALL_VALUE : true
      )
      if (
        cleanedCurrent.length === allOptions.length &&
        allOptions.length > 0
      ) {
        // Deselect all (empty = "no filter" semantics; UI shows none selected)
        updateFilter(key as any, [])
      } else {
        // Select all (never persist SELECT_ALL_VALUE in state)
        updateFilter(key as any, allOptions as any)
      }
    },
    [updateFilter]
  )

  const getFilteredValue = useCallback(
    (
      key: 'carrierNames' | 'paymentTypes' | 'statuses' | 'clientPublicKeys',
      allOptions: Array<string | number>
    ): Array<string | number> => {
      const value = (filters as any)[key] ?? []
      const cleanedValue = (Array.isArray(value) ? value : []).filter((v) =>
        typeof v === 'string' ? v !== SELECT_ALL_VALUE : true
      )
      if (value.length === 0) {
        return allOptions // All selected by default
      }
      return cleanedValue
    },
    [filters]
  )

  const clearAllFilters = useCallback(() => {
    // Mirror the "Total Assigned" (MAKER) / "Total Cases" (ESCALATOR/CHECKER) preset:
    // scope to self for MAKER/ESCALATOR, no status filter for any role.
    const isMaker = role === PaymentAssignmentRole.MAKER
    const isEscalator = role === PaymentAssignmentRole.ESCALATOR
    setFilters((prev) => ({
      paymentPeriod: getDefaultPaymentPeriod(),
      benefitEffectiveDateFrom: undefined,
      benefitEffectiveDateTo: undefined,
      benefitEndDateFrom: undefined,
      benefitEndDateTo: undefined,
      carrierNames: [],
      paymentTypes: [],
      priorityLevels: [],
      statuses: [],
      clientPublicKeys: [],
      assigneeIds:
        isMaker && lockedAssigneeId != null
          ? [lockedAssigneeId]
          : isMaker
            ? prev.assigneeIds
            : [],
      escalatorIds:
        isEscalator && lockedEscalatorId != null ? [lockedEscalatorId] : [],
      paymentSubmittedByIds: [],
      primaryPaymentMethods: [],
      search: undefined,
      testUserFilter: DEFAULT_TEST_USER_FILTER,
      terminatedFilter: TerminatedFilter.ALL,
      relationships: [],
      onlyMigrated: undefined,
    }))
  }, [role, lockedAssigneeId, lockedEscalatorId])

  return {
    filters,
    updateFilter,
    handleSelectAll,
    getFilteredValue,
    clearAllFilters,
    SELECT_ALL_VALUE,
  }
}
